import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { HttpStatusCode } from 'axios';
import { NotAuthenticatedError } from './AxiosWrapper';
import { reportMutationError, reportQueryError } from './reportApiError';
import { ApiError } from './unwrapApiResponse';

const MAX_QUERY_RETRIES = 3;

// Transient 4xx that are worth retrying (unlike deterministic 403/404/422):
// 408 Request Timeout and 429 Too Many Requests both clear on their own.
const RETRYABLE_4XX = new Set<number>([
  HttpStatusCode.RequestTimeout,
  HttpStatusCode.TooManyRequests,
]);

/**
 * Skip retries only for failures we KNOW are deterministic; keep React Query's
 * default retry-3× for everything else. Now that queryFns throw on failure, a
 * deterministic 4xx (the dead-stack 403/404 that motivated this work, or a
 * validation error) would otherwise be refetched 4× over several seconds
 * before the error UI appears, and a lapsed session can't be fixed by
 * refetching at all. Errors this policy can't classify — plain Errors from
 * hooks that haven't adopted unwrapApiResponse (e.g. React Query's own "data
 * is undefined" when AxiosWrapper resolves null on a network blip), or an
 * AuthTokenError from an Auth0 hiccup — are indistinguishable from transient,
 * so they keep the default-client retry behavior this client replaced.
 */
const retryTransientOnly = (failureCount: number, error: unknown): boolean => {
  if (failureCount >= MAX_QUERY_RETRIES) return false;
  if (error instanceof NotAuthenticatedError) return false;
  if (error instanceof ApiError) {
    return (
      error.status === undefined ||
      error.status >= HttpStatusCode.InternalServerError ||
      RETRYABLE_4XX.has(error.status)
    );
  }
  return true;
};

/**
 * App-wide QueryClient. The QueryCache / MutationCache onError hooks are the
 * single place API failures are reported to Sentry — AxiosWrapper no longer
 * captures, which removes the cross-layer double-reporting and the
 * per-retry-attempt amplification. onError fires once per query/mutation
 * after retries are exhausted, so one outage produces one Sentry issue.
 * Filtering and tagging live in reportApiError.ts.
 */
export const createAppQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: { retry: retryTransientOnly },
    },
    queryCache: new QueryCache({
      onError: (error, query) => reportQueryError(error, query.queryKey),
    }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation) =>
        reportMutationError(error, mutation.options.mutationKey),
    }),
  });
